import { IBCS } from './HatchPattern';
import { formatEur } from './IbcsTooltip';

/**
 * IBCS Bullet-Chart for CreditLine utilization.
 * Target (limit) is the outlined box; actual (utilised) is the solid fill.
 */
interface BulletCreditLineProps {
    label: string;
    sublabel?: string;
    utilised: number;
    limit: number;
    currencyCode: string;
    thresholds?: { warn: number; critical: number }; // pct of limit
}

export default function BulletCreditLine({
    label, sublabel, utilised, limit, currencyCode, thresholds = { warn: 70, critical: 90 },
}: BulletCreditLineProps) {
    const pct = limit > 0 ? (utilised / limit) * 100 : 0;
    const clamped = Math.min(Math.max(pct, 0), 100);
    const severity = pct >= thresholds.critical ? 'critical' : pct >= thresholds.warn ? 'warn' : 'ok';
    const fillColor = severity === 'critical' ? IBCS.negative : severity === 'warn' ? IBCS.forecastAccent : IBCS.positive;

    return (
        <div className="flex items-center gap-3 py-1.5">
            <div className="w-48 flex-shrink-0">
                <div className="font-medium text-sm text-gray-900 truncate">{label}</div>
                {sublabel && <div className="text-xs text-gray-500 truncate">{sublabel}</div>}
            </div>
            <div className="flex-1 relative h-6 border border-gray-400 bg-white">
                {/* Warning threshold guide */}
                <div
                    className="absolute top-0 bottom-0 border-r border-dashed border-gray-300"
                    style={{ left: `${thresholds.warn}%` }}
                />
                {/* Critical threshold guide */}
                <div
                    className="absolute top-0 bottom-0 border-r border-dashed border-gray-400"
                    style={{ left: `${thresholds.critical}%` }}
                />
                {/* Utilisation fill */}
                <div
                    className="absolute top-0 bottom-0 left-0 transition-all"
                    style={{ width: `${clamped}%`, backgroundColor: fillColor }}
                />
                <div className="absolute inset-0 flex items-center justify-center text-[11px] font-mono text-gray-900 mix-blend-difference pointer-events-none">
                    <span className="bg-white/80 px-1 rounded">{pct.toFixed(1)}%</span>
                </div>
            </div>
            <div className="w-48 text-right flex-shrink-0 text-xs">
                <div className="font-mono">
                    {formatEur(utilised, false).replace(' €', '')} / {formatEur(limit, false).replace(' €', '')} {currencyCode}
                </div>
                <div className="text-gray-500">Headroom: {formatEur(limit - utilised, false).replace(' €', '')} {currencyCode}</div>
            </div>
        </div>
    );
}
